import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import helpers from "helpers/index";
import queryClient, { IQueryParams } from 'queries';
import { TypedVehicle } from "./vehicle.query";
import { TypedTripData } from "./trip.query";
import { TypedSeating } from "./seating.query";
import { TypedTrip_manifest_ticket } from "./trip_manifest_ticket.query";
import { TypedTrip_manifest_invoice } from "./trip_manifest_invoice.query";

export interface TypedTripManifestDriver {
    "id"?: string,
    "slug"?: string,
    "trip_manifest_id"?: string,
    "driver_id"?: bigint,
    "driver_role"?: string, // driver: lái chính hoặc assistant: lái phụ
    "createdAt"?: string,
    "driver_data"?: {
        "user_id"?: bigint,
        "display_name"?: string,
        "user_phonenumber"?: string
    }
}

export enum ENUM_TRIP_MANIFEST_STATUS {
    DRAFT = 0,
    DISPATCHED = 1,
    IN_TRANSIT = 2,
    COMPLETED = 3,
    FINALIZED = 4,
    CANCELLED = 9,
}

// Đối tượng này ánh xạ enum sang chuỗi văn bản tương ứng
export const tripStatusLabels: { [key in ENUM_TRIP_MANIFEST_STATUS]: string } = {
    [ENUM_TRIP_MANIFEST_STATUS.DRAFT]: 'Bản nháp',
    [ENUM_TRIP_MANIFEST_STATUS.DISPATCHED]: 'Đã điều chuyến',
    [ENUM_TRIP_MANIFEST_STATUS.IN_TRANSIT]: 'Đang trong chuyến',
    [ENUM_TRIP_MANIFEST_STATUS.COMPLETED]: 'Hoàn thành',
    [ENUM_TRIP_MANIFEST_STATUS.FINALIZED]: 'Đã chốt lệnh',
    [ENUM_TRIP_MANIFEST_STATUS.CANCELLED]: 'Hủy chuyến',
};
export const tripStatusHelptext: { [key in ENUM_TRIP_MANIFEST_STATUS]: string } = {
    [ENUM_TRIP_MANIFEST_STATUS.DRAFT]: 'Chỉ admin nhìn thấy bản nháp',
    [ENUM_TRIP_MANIFEST_STATUS.DISPATCHED]: 'Tài xế sẽ nhận được thông báo',
    [ENUM_TRIP_MANIFEST_STATUS.IN_TRANSIT]: 'Tài xế đã bắt đầu chuyến',
    [ENUM_TRIP_MANIFEST_STATUS.COMPLETED]: 'Tài xế đánh dấu là hoàn thành chuyến',
    [ENUM_TRIP_MANIFEST_STATUS.FINALIZED]: 'Tài xế xác nhận đã kiểm tra và chốt lệnh',
    [ENUM_TRIP_MANIFEST_STATUS.CANCELLED]: '',
};

export enum ENUM_CALL_STATUS {
    UNTOUCHED = 'UNTOUCHED',
    NOT_CALLED = 'NOT_CALLED',
    INVALID_NUMBER = 'INVALID_NUMBER',
    COMPLETED = 'COMPLETED',
    NO_ANSWER = 'NO_ANSWER',
}

export const enum ENUM_ORDERS_STATUS {
    AWAITING_PICKUP = 'AWAITING_PICKUP',
    PICKED_UP = 'PICKED_UP',
    DROPPED_OFF = 'DROPPED_OFF',
    CANCELLED = 'CANCELLED'
}


export interface TypedTripManifest {
    id?: string;
    time_id?: string;
    departure_date?: string;
    departure_hour?: string;
    seating_id?: string;
    vehicle_id?: string;
    /**
     * trip_status bao gồm: 0: bản nháp, 1: đã điều chuyến, 2: đang trong chuyến, 3: Hoàn thành, 9: hủy chuyến, không thực hiện
     */
    trip_status?: number;
    /**
     * trip_type: mainline là chuyến chính, shuttle là chuyến chung chuyển, đi gom khách khắp nơi xong về chuyến chính ...
     */
    trip_type?: string;
    trip_manifest_parent?: string;
    createdBy?: string;
    createdAt?: string;

    total_fare?: number,
    total_fee?: number,
    total_amount_due?: number;
    total_occupied_seats?: number,

    /** extra fields */
    odo_from?: number,
    odo_to?: number,

    "seating"?: TypedSeating,
    "trip"?: TypedTripData,
    "vehicle"?: TypedVehicle,
    trip_manifest_driver?: TypedTripManifestDriver[],
    trip_manifest_ticket?: TypedTrip_manifest_ticket[],

    /** Include luôn invoice vào ... */
    trip_manifest_invoice?: TypedTrip_manifest_invoice[],


    is_exported_invoice?: number /** 0 là chưa xuất, 1 là xuất ồi */
}

type IQuery = TypedTripManifest & IQueryParams;


/*
* List of all trip manifest
*/

export function useGetTrip_manifests(object: IQuery) {
    const EndURL = helpers.buildEndUrl(object);
    return useQuery({
        queryKey: ["trip_manifest/fetch_entity_list", object],
        queryFn: () => axios.get<{
            sum: {
                countTrip: number,
                countVehicle: number;
                totalPassenger: number;
                totalTicket: number;
                totalMoney: number;
            }
            , records: TypedTripManifest[]
        }>(`/trip_manifest/${EndURL}`).then(response => {
            let { data, headers } = response;
            return {
                sum: data.sum,
                records: data.records,
                totalItems: Number(headers['x-total-count'] || 0)
            }
        }),
        retry: 1,
    });
}

export function useGetTrip_manifest(trip_manifest_id: any) {
    return useQuery({
        queryKey: ["trip_manifest/fetch_entity", trip_manifest_id],
        queryFn: () => axios.get<TypedTripManifest>(`/trip_manifest/${trip_manifest_id}`).then(response => response.data),
        retry: 1,
        enabled: !!trip_manifest_id
    });
}

export function useMutateRefreshTrip_manifest() {
    return useMutation({
        mutationKey: ["trip_manifest/refresh_fetch_entity"],
        mutationFn: (trip_manifest_id: string) => axios.get<TypedTripManifest[]>(`/trip_manifest/refresh/${trip_manifest_id}`).then(response => response.data),
        retry: 1,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
        }
    });
}

export function useMutateUpdateTrip_manifest() {
    return useMutation({
        mutationKey: ["trip_manifest/update_entity"],
        mutationFn: ({ id, ...rest }: TypedTripManifest) => axios.patch<TypedTripManifest>(`/trip_manifest/${id}`, rest).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
        },
        retry: 1,
    });
}

export function useMutateUpdateTrip_manifest_seating_template() {
    return useMutation({
        mutationKey: ["trip_manifest/update_entity_seating_template"],
        mutationFn: ({ id, ...rest }: TypedTripManifest) => axios.patch<TypedTripManifest>(`/trip_manifest/update_seating_template/${id}`, rest).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
        },
        retry: 1,
    });
}

/** 
 * Tạo tối thiểu: Post lên 
 * {
    "time_id": 160, // id của time_id
    "trip_type": "mainline" // hoặc shuttle
}
 */


export function useMutateCreateTrip_manifest() {
    return useMutation({
        mutationKey: ['trip_manifest/create_entity'],
        mutationFn: (body: TypedTripManifest) => axios.post(`/trip_manifest`, body).then((response) => response.data),
        onSuccess: () => {
            /** Load lại trip */
            // queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity_list'] });
            // queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        }
    });
}

/** Chỉ xóa bản nháp thôi nhá */
export function useMutateDeleteTrip_manifest() {
    return useMutation({
        mutationKey: ['trip_manifest/delete_entity'],
        mutationFn: (trip_manifest_id: string) => axios.delete(`/trip_manifest/${trip_manifest_id}`).then((response) => response.data),
        onSuccess: () => {
            /** Load lại trip */
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
        }
    });
}



/**** Các chức năng khác ... */

/** THÊM Tài vào chuyến */
// {
// "trip_manifest_id": 1,
//     "driver_id": 2,
//     "driver_role": "assistant" // driver
// }
export function useMutateAssign_driver_to_trip() {
    return useMutation({
        mutationKey: ['trip_manifest_driver/create_entity'],
        mutationFn: (body: { trip_manifest_id: string, driver_id: string, driver_role: string }) => axios.post(`/trip_manifest_driver`, body).then((response) => response.data),
        onSuccess: () => {
            /** Load lại trip */
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
        }
    });
}

/** Xóa tài xế đi  */
export function useMutateRemove_driver_from_trip() {
    return useMutation({
        mutationKey: ['trip_manifest_driver/delete_entity'],
        mutationFn: (trip_manifest_driver_id: string) => axios.delete<any>(`/trip_manifest_driver/${trip_manifest_driver_id}`).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
        }
    });
}


/** *********************************** */


/** 
 * Chuyển sang giờ chạy khác
 * Cơ chế như sau nhá, nếu giờ có rồi thì nhét vào ID,còn chưa có thì tạo ID xong nhét vào ... done
 */
export function useMutateToggleLockTripManifest() {
    return useMutation({
        mutationKey: ['trip_manifest_driver/toggle_lock_trip'],
        mutationFn: (trip_manifest_id) => axios.patch(`/trip_manifest/toggle_lock_trip/${trip_manifest_id}`).then((response) => response.data),
        onSuccess: () => {
            /** Load lại trip */
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
        }
    });
}